import { CommonModule } from '@angular/common';
import { Component, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { Observable } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiSelectOption, OuiTextfield } from 'omnium-ui/form-field';
import { OuiSelectFilter } from 'omnium-ui/select-filter';
import { OuiTableModule } from 'omnium-ui/table';
import { ConsultantCardComponent } from '../../../../core/components/consultant-card/consultant-card.component';
import { ConsultantViewerSearchPage } from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { ConsultantStatusTagComponent } from '../../shared/consultant-status-tag/consultant-status-tag.component';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { AtlasViewerSearchPageBase, atlasViewerSearchPaginatorProvider } from '../shared/atlas-viewer-search-page.base';

@Component({
  selector: 'dbw-atlas-consultant-search-page',
  imports: [
    CommonModule,
    MatPaginatorModule,
    MatTableModule,
    OuiButtonModule,
    OuiSelectFilter,
    OuiTableModule,
    OuiTextfield,
    ConsultantCardComponent,
    ConsultantStatusTagComponent,
    StateCardComponent,
  ],
  providers: [atlasViewerSearchPaginatorProvider],
  templateUrl: './consultant-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './consultant-search.component.scss',
})
export class AtlasConsultantSearchPageComponent extends AtlasViewerSearchPageBase<ConsultantViewerSearchPage> {
  readonly displayedColumns = ['consultant', 'status', 'affiliationDate', 'resiliationDate', 'qualification'];
  readonly consultantIdFilter = signal('');
  readonly consultantCodeFilter = signal('');
  readonly consultantNameFilter = signal('');
  readonly arianeIdFilter = signal('');
  readonly consultantStatusFilter = signal<string[]>([]);
  readonly consultantStatusOptions: OuiSelectOption<string>[] = [
    { label: 'Consultant prospect', value: '0' },
    { label: 'Affilié', value: '1' },
    { label: 'Résilié', value: '2' },
    { label: 'Radié', value: '3' },
    { label: 'Apprenti', value: '4' },
    { label: 'Apprenti prospect', value: '5' },
    { label: 'Apprenti résilié', value: '6' },
  ];

  constructor(private readonly apiService: DoublonsBusterApiService) {
    super();
  }

  onConsultantIdFilterChange(value: string): void {
    this.consultantIdFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantCodeFilterChange(value: string): void {
    this.consultantCodeFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantNameFilterChange(value: string): void {
    this.consultantNameFilter.set(value);
    this.queueFilterReload();
  }

  onArianeIdFilterChange(value: string): void {
    this.arianeIdFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantStatusFilterChange(value: string[]): void {
    this.consultantStatusFilter.set(value);
    this.queueFilterReload();
  }

  override hasActiveFilters(): boolean {
    return Boolean(
      this.consultantIdFilter().trim() ||
      this.consultantCodeFilter().trim() ||
      this.consultantNameFilter().trim() ||
      this.arianeIdFilter().trim() ||
      this.consultantStatusFilter().length > 0
    );
  }

  getQualificationLabel(row: { qualificationCode?: string | null; qualificationLabel?: string | null }): string {
    return row.qualificationLabel?.trim() || row.qualificationCode?.trim() || '-';
  }

  protected override loadPageRequest(pageIndex: number, pageSize: number): Observable<ConsultantViewerSearchPage> {
    return this.apiService.searchAtlasConsultants(pageIndex, pageSize, {
      consultantId: this.parseOptionalNumber(this.consultantIdFilter()),
      consultantCode: this.consultantCodeFilter(),
      consultantName: this.consultantNameFilter(),
      arianeId: this.parseOptionalNumber(this.arianeIdFilter()),
      consultantStatuses: this.consultantStatusFilter(),
    });
  }

  protected override getLoadErrorMessage(error: unknown): string {
    const httpError = error as { error?: { detail?: string } };
    return httpError?.error?.detail ?? 'Impossible de rechercher les consultants Atlas.';
  }
}
